import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getFirstAppliedFilter,
  intervalSelector,
  supportedMetricsSelector,
} from '@store/common/selectors';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { createFilterRule, checkIfRuleAlreadyExist } from '@utils/filter';
import {
  getAppEventsPreset,
  getServerSidePerformancePreset,
} from '@src/components/common/Dashboard/UseCases/configs/eco';
import { networkRequestUrlDimensions } from '@src/utils/table/rcaUtils';
import {
  getValidateDimensionFunc,
  getValidateMetricFunc,
} from '@src/components/common/Dashboard/UseCases/utils';
import { drilldownDashboardType } from '@src/components/common/Dashboard/UseCases';
import { WIDGET_DRILLDOWN } from '@src/utils/sensor/track';
import apptracker from '@src/utils/sensor/apptracker';

const getPresetConfigFunc = (target: 'appEvents' | 'serverSidePerformance') => {
  return {
    appEvents: getAppEventsPreset,
    serverSidePerformance: getServerSidePerformancePreset,
  }[target];
};

export const useRCADrilldown = ({ rowData }: any) => {
  const metrics = useSelector(supportedMetricsSelector) as object;
  const dimensionList = useSelector(allDimensionsListSelector) as [];

  const globalFilter = useSelector(getFirstAppliedFilter);
  const formattedInterval = useSelector(intervalSelector);

  const dispatch = useDispatch();
  const dimension = useMemo(() => Object.values(networkRequestUrlDimensions), []);
  const targetPreset = 'serverSidePerformance';

  const handleDrilldown = useCallback(() => {
    const crumbFilters = [globalFilter];
    const ruleList: any[] = [];

    dimension.forEach((d) => {
      const dimensionInfo = (dimensionList as any).find((it) => it.groupBy === d);
      if (!dimensionInfo) {
        return;
      }

      if (!rowData[d]) {
        return;
      }

      const filterByValue = createFilterRule(dimensionInfo, rowData[d], rowData[d]);

      /** The logic of handling drilldown and globalFilter is referred from
       *  function dimensionDrillown to ensure the structure is consistent. */
      const doesRuleExist = checkIfRuleAlreadyExist(globalFilter.rule, filterByValue);
      if (doesRuleExist) {
        return;
      }
      ruleList.push([filterByValue]);
    });

    const lastRules = getRulesFromFilter(ruleList, true);
    const lastFilter = getFilterFromRules(lastRules, true);
    crumbFilters.push(lastFilter);

    const combinedRules = getRulesFromFilter([...globalFilter.rule, ...lastFilter.rule], true);
    const combinedFilter = getFilterFromRules(combinedRules, true);

    const getPresetConfig = getPresetConfigFunc(targetPreset);
    if (!getPresetConfig) {
      return;
    }

    const { config, title, key } = getPresetConfig({
      validateDimension: getValidateDimensionFunc(dimensionList),
      validateMetric: getValidateMetricFunc(metrics),
    });

    dispatch(
      createTempDashboard(
        drilldownDashboardType,
        {
          ...config,
          common: {
            appliedFilters: [combinedFilter],
            interval: formattedInterval,
          },
          summary: {
            drilldownData: crumbFilters,
          },
          usecaseKey: key,
        },
        title,
      ),
    );

    apptracker(WIDGET_DRILLDOWN, { title });
  }, [globalFilter, dimension, metrics, dispatch, formattedInterval, dimensionList, rowData]);

  return { handleDrilldown };
};
